import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';
import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';

// Axios instance for delivery challans API
const deliveryChallanAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: warehouseParamsSerializer,
});

// Attach auth token if available
deliveryChallanAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) config.headers.Authorization = `Bearer ${token}`;
    return applyWarehouseFilter(config);
  },
  (error) => Promise.reject(error)
);

// Redirect on auth failure
deliveryChallanAPI.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

const BASE_URL = '/delivery-challans';

// Caching helpers (memory + sessionStorage)
const CACHE_PREFIX = 'erp_delivery_challans_cache_v1';
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 minutes

const memoryCache = new Map();

function cacheKey(key) {
  return `${CACHE_PREFIX}::${key}`;
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(cacheKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expires) return null;
    if (Date.now() > parsed.expires) {
      sessionStorage.removeItem(cacheKey(key));
      return null;
    }
    return parsed.value;
  } catch (e) {
    return null;
  }
}

function writeCache(key, value, ttl = DEFAULT_TTL_MS) {
  try {
    sessionStorage.setItem(cacheKey(key), JSON.stringify({ value, expires: Date.now() + ttl }));
  } catch (e) { }
}

function readMemoryCache(key) {
  const entry = memoryCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expires) {
    memoryCache.delete(key);
    return null;
  }
  return entry.value;
}

function writeMemoryCache(key, value, ttl = DEFAULT_TTL_MS) {
  memoryCache.set(key, { value, expires: Date.now() + ttl });
}

function deleteCache(key) {
  try { sessionStorage.removeItem(cacheKey(key)); } catch (e) { }
  memoryCache.delete(key);
}

// API Methods
const deliveryChallanService = {
  // Get all delivery challans
  async getAllDeliveryChallans(options = {}) {
    const { page = 1, limit = 100, force = false } = options;
    const key = `all_delivery_challans_${page}_${limit}`;

    if (!force) {
      const cached = readCache(key) || readMemoryCache(key);
      if (cached) return { success: true, data: cached };
    }

    try {
      const response = await deliveryChallanAPI.get(`${BASE_URL}`);
      const data = response.data;

      writeCache(key, data);
      writeMemoryCache(key, data);

      return { success: true, data };
    } catch (error) {
      console.error('Failed to fetch delivery challans:', error);
      const errorMsg = extractApiError(error);
      return { success: false, error: errorMsg };
    }
  },

  // Update delivery challan
  async updateDeliveryChallan(challanId, payload) {
    try {
      const response = await deliveryChallanAPI.put(`${BASE_URL}/${challanId}`, payload);
      const data = response.data;

      // Update caches
      const singleKey = `delivery_challan_${challanId}`;
      writeCache(singleKey, data);
      writeMemoryCache(singleKey, data);

      // Clear list cache to force refresh
      const listKeys = Array.from(memoryCache.keys()).filter(k => k.startsWith('all_delivery_challans_'));
      listKeys.forEach(k => deleteCache(k));

      return { success: true, data };
    } catch (error) {
      console.error('Failed to update delivery challan:', error);
      const errorMsg = extractApiError(error);
      return { success: false, error: errorMsg };
    }
  },

  async getUnprinted(params = {}) {
    try {
      const response = await deliveryChallanAPI.get('/delivery-challans/unprinted', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async markChallanPrinted(subOrderId) {
    try {
      const response = await deliveryChallanAPI.post(`/delivery-sub-orders/${subOrderId}/mark-challan-printed`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },
};

export default deliveryChallanService;